// The answer-variant list inputs, and the open-ended block each open variant draws.
//
// A variant's block is a question in its own right as far as the report params go: it carries the
// key and id the retired sub-item view derived, and so its own oe/cm/z/s sections off the key and
// its cloud off the id. The shell reads those params and hands them over; everything the block
// needs beyond them is built here, off the question this list hangs on.

import { OE_CHART_VIEW_MODES, OE_HEALS_VIEW_MODE } from '@aytm/app/stats/page/question-views/oe/lib/family'
import {
  answerVariantId,
  answerVariantKey
} from '@aytm/app/stats/page/question-views/answer-variants/lib/props/variants'

// The variants that can be opened at all: one carrying comments, or a positive open answer.
export const calcExpandable = ({ answerVariants }) =>
  (answerVariants || []).filter(av => av && (av.comments || av.positive))

// the key and id every report param a variant owns hangs off -- one format, owned by answer-variants
export { answerVariantId, answerVariantKey }

// A variant reads the slice of the answer that is its own; a question still loading has none yet,
// and neither has a variant whose own fetch is out.
const answerAt = (answer, position, loadingRows) => {
  if ('loading' === answer || loadingRows?.has(String(position))) return 'loading'

  return answer?.[`a${position}`] || { total: 0 }
}

export const buildAnswerVariantProps =
  ({
    questionId,
    questionKey,
    type,
    format,
    components,
    answer,
    modelDefaults,
    clickable,
    disabled,
    questionChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    batch,
    link,
    isReportXpert,
    answerVariantParams = {},
    loadingRows,
    handlers = {}
  } = {}) =>
  answerVariant => {
    const position = answerVariant?.position
    if (null == position) return null

    const id = answerVariantId(questionId, position)
    const key = answerVariantKey(questionKey, position)

    return {
      id,
      parentQuestionId: questionId,
      questionKey: key,
      type,
      position,
      // a variant draws under the question's sub-item settings where it has them
      format: format?.subitems || format,
      // the variant's verbatims already sit under the row naming them, so the count that closes a
      // standalone open-ended question would only repeat what the list above says
      components: { ...(components?.subitems || components), total: false },
      answer: answerAt(answer, position, loadingRows),
      answerVariant,
      clickable,
      disabled,
      questionChartMode,
      defaultSortOrder,
      reportPercentDecimals,
      chartViewModes: OE_CHART_VIEW_MODES,
      healViewMode: OE_HEALS_VIEW_MODE,
      modelDefaults,
      params: answerVariantParams[position] || {},
      labelTarget: { id, questionId, batch },
      commentsTarget: { id, questionId, link, batch, answerPosition: position, isReportXpert },
      // an image export is serialized by the shell, which names the file after the position it is
      // given -- a variant's cloud has to name itself, not the question it hangs on
      handlers: {
        ...handlers,
        // a label inside a variant addresses nothing the filters can name, so it does not offer one
        onLabelClick: null,
        onWcExport: (params, data, callback) => handlers.onWcExport?.({ ...params, position: key }, data, callback)
      },
      // the identity travels with the api: the exporters read a variant as a question of its own,
      // and there is no model of its own left to read it off
      onReady: api =>
        handlers.onAnswerVariantReady?.(position, api, {
          id,
          key,
          type,
          position,
          uid: answerVariant.uid,
          answerVariant
        }),
      onStateChange: (reason, options) => handlers.onAnswerVariantStateChange?.(position, key, reason, options),
      onWcParamsChange: params => handlers.onAnswerVariantWcParamsChange?.(id, params),
      onOutdatedPreset: () => handlers.onAnswerVariantStateChange?.(position, key, 'state', {})
    }
  }

export const calcAnswerVariantsProps = ({
  id,
  type,
  format,
  components,
  answerVariants,
  uncollapsed,
  oePropsOf
} = {}) => ({
  questionId: id,
  type,
  answerVariants,
  answerLabels: format?.answerLabels,
  expandable: undefined === components?.expand || Boolean(components.expand),
  uncollapsed,
  oePropsOf
})
